import Cookies from 'js-cookie'
import { createRouter, createWebHistory } from 'vue-router'

import CustomerRecordLayout from '@/components/customers/CustomerRecordLayout.vue'
import { websiteLeadsPath, websiteReportPath } from '@/composables/useAgentSections'
import { hasFulfillmentWizard } from '@/composables/useFulfillmentWizardAccess'
import { posthog } from '@/composables/usePosthog'
import { newAgentPath, slugForAgentType } from '@/config/agentCards'
import { WIZARD_PATH as AGENT_WIZARD_PATH } from '@/config/agentWizardSteps'
import { customerRevenueRoute, customerTabRoutes } from '@/config/customerTabs'
import { WIZARD_PATH as DROP_WIZARD_PATH } from '@/config/dropWizardSteps'
import { WIZARD_PATH as FULFILLMENT_WIZARD_PATH } from '@/config/fulfillmentWizardSteps'
import { MEMBERSHIP_FEATURE } from '@/config/membershipTierView'
import { reportDetailByPath } from '@/config/reportGroups'
import { ORDERING_AND_DELIVERY_PATH } from '@/config/settingsTabs'
import { PARSLEY_NEW_ITEMS_PATH, PARSLEY_PATH } from '@/helpers/parsley'
import { captureReferralCode } from '@/helpers/referralCode'
import { captureSignupAttribution } from '@/helpers/signupAttribution'
import { buildFeatureGuard } from '@/router/featureGuard'
import { franchiseDayRoutes } from '@/router/franchiseDayRoutes'
import { franchiseOnlyGuard } from '@/router/franchiseOnlyGuard'
import { buildParsleyGuard } from '@/router/parsleyGuard'
import { leadCaptureGuard } from '@/router/revealGuards'
import { buildSearchAndAiGuard } from '@/router/searchAndAiGuard'
import { buildTierListGuard } from '@/router/tierListGuard'
import { buildTierWizardGuards } from '@/router/tierWizardGuard'
import { canAccessRouteForPlatform } from '@/services/platformRouting'
import privilegeService from '@/services/privilege.service'
import store from '@/store'
import AutomationDropList from '@/view/automations/AutomationDropList.vue'
import ScheduledMessageList from '@/view/automations/ScheduledMessageList.vue'
import SentMessageDetail from '@/view/automations/SentMessageDetail.vue'
import SentMessageList from '@/view/automations/SentMessageList.vue'
import WorkflowList from '@/view/automations/WorkflowList.vue'
import AgentSettingsIndex from '@/view/catering/AgentSettingsIndex.vue'
import CateringLeads from '@/view/catering/CateringLeads.vue'
import FeatureLocked from '@/view/FeatureLocked.vue'
import DashboardHome from '@/view/franchise/DashboardHome.vue'
import FranchiseAccount from '@/view/franchise/FranchiseAccount.vue'
import FranchiseAvailability from '@/view/franchise/FranchiseAvailability.vue'
import ProductLibraryRoute from '@/view/franchise/ProductLibraryRoute.vue'
import LinksNeue from '@/view/LinksNeue.vue'
import MarketingHistory from '@/view/marketing/MarketingHistory.vue'
import MarketingHome from '@/view/marketing/MarketingHome.vue'
import MarketingPerformance from '@/view/marketing/MarketingPerformance.vue'
import MarketingShell from '@/view/marketing/MarketingShell.vue'
import ResellerDetail from '@/view/resellers/ResellerDetail.vue'
import ResellerList from '@/view/resellers/ResellerList.vue'
import SettingsAccount from '@/view/settings/SettingsAccount.vue'
import SettingsBilling from '@/view/settings/SettingsBilling.vue'
import SettingsCompany from '@/view/settings/SettingsCompany.vue'
import SettingsIndex from '@/view/settings/SettingsIndex.vue'
import SettingsIntegrations from '@/view/settings/SettingsIntegrations.vue'
import SettingsNotifications from '@/view/settings/SettingsNotifications.vue'
import SettingsOfficeHours from '@/view/settings/SettingsOfficeHours.vue'
import SettingsOrderingDelivery from '@/view/settings/SettingsOrderingDelivery.vue'
import SettingsSearchAndAi from '@/view/settings/SettingsSearchAndAi.vue'
import ParsleyMapping from '@/view/store/ParsleyMapping.vue'
import LeadCaptureReport from '@/view/storefront/LeadCaptureReport.vue'
import LeadCaptureSettings from '@/view/storefront/LeadCaptureSettings.vue'

import AudienceNeue from '../components/neue/AudienceNeue'
import AutomationNeue from '../components/neue/AutomationNeue'
import FulfillmentMethodNeue from '../components/neue/FulfillmentMethodNeue'
import StorePrefs from '../components/StorePrefs'
import OrderDetailNeue from '../view/activity/OrderDetailNeue'
import OrdersNeue from '../view/activity/OrdersNeue'
import AgentConversations from '../view/ai/AgentConversations'
import AgentForm from '../view/ai/AgentForm'
import AgentGeneralSettings from '../view/ai/AgentGeneralSettings'
import AgentList from '../view/ai/AgentList'
import AgentWebsite from '../view/ai/AgentWebsite'
import AgentWizard from '../view/ai/agentWizard/AgentWizard.vue'
import WebsiteChannelReport from '../view/ai/WebsiteChannelReport'
import Auth from '../view/Auth'
import ConfirmEmailAddress from '../view/ConfirmEmailAddress.vue'
import CustomerMembershipTab from '../view/customers/CustomerMembershipTab'
import CustomerOrdersTab from '../view/customers/CustomerOrdersTab'
import CustomerRevenueReport from '../view/customers/CustomerRevenueReport'
import CustomerSettingsTab from '../view/customers/CustomerSettingsTab'
import ListsNeue from '../view/customers/ListsNeue'
import MessagesNeue from '../view/customers/MessagesNeue'
import MessagesTab from '../view/customers/MessagesTab'
import NewListNeue from '../view/customers/NewListNeue'
import ProfileTab from '../view/customers/ProfileTab'
import ForgotPassword from '../view/ForgotPassword'
import ForgotPasswordEmail from '../view/ForgotPasswordEmail'
import GuidesTab from '../view/GuidesTab'
import Login from '../view/Login'
import ModulesNeue from '../view/ModulesNeue'
import PackingSlipSettings from '../view/PackingSlipSettings'
import PayPastDueStripeBalance from '../view/PayPastDueStripeBalance'
import ReportDetailPage from '../view/ReportDetailPage'
import ReportsNeue from '../view/ReportsNeue'
import RestartStripeIntegration from '../view/RestartStripeIntegration'
import SetPassword from '../view/SetPassword'
import SignupAreaCode from '../view/SignupAreaCode'
import SignupBusinessName from '../view/SignupBusinessName'
import SignupBusinessType from '../view/SignupBusinessType'
import SignupEmail from '../view/SignupEmail'
import SignupPassword from '../view/SignupPassword'
import SignupReferral from '../view/SignupReferral'
import StandardLayout from '../view/StandardLayout'
import AccountCreditList from '../view/store/AccountCreditList'
import DiscountCodeDetail from '../view/store/DiscountCodeDetail'
import DiscountCodeList from '../view/store/DiscountCodeList'
import DiscountCodeNew from '../view/store/DiscountCodeNew'
import DropDetail from '../view/store/DropDetail'
import DropList from '../view/store/DropList'
import DropNew from '../view/store/DropNew'
import DropWizard from '../view/store/dropWizard/DropWizard'
import FulfillmentMethodList from '../view/store/FulfillmentMethodList'
import FulfillmentMethodWizard from '../view/store/fulfillmentWizard/FulfillmentMethodWizard'
import GiftCardDetail from '../view/store/GiftCardDetail'
import GiftCardList from '../view/store/GiftCardList'
import GiftCardNew from '../view/store/GiftCardNew'
import LabelDetail from '../view/store/LabelDetail'
import LabelList from '../view/store/LabelList'
import LabelNew from '../view/store/LabelNew'
import LoyaltyProgramOverview from '../view/store/LoyaltyProgramOverview'
import LoyaltyProgramSettings from '../view/store/LoyaltyProgramSettings'
import MembershipTierDetail from '../view/store/MembershipTierDetail'
import MembershipTierList from '../view/store/MembershipTierList'
import MembershipTierNew from '../view/store/MembershipTierNew'
import MembershipTierOverview from '../view/store/membershipTierOverview/MembershipTierOverview'
import MembershipTierRoute from '../view/store/membershipTierWizard/MembershipTierRoute'
import MembershipTierWizard from '../view/store/membershipTierWizard/MembershipTierWizard'
import ProductDetail from '../view/store/ProductDetail'
import ProductFolders from '../view/store/ProductFolders'
import ProductNeue from '../view/store/ProductNeue'
import ProductNew from '../view/store/ProductNew'
import ReferralCodeDetail from '../view/store/ReferralCodeDetail'
import ReferralNeue from '../view/store/ReferralNeue'
import ReferralOverview from '../view/store/ReferralOverview'
import SellNeue from '../view/store/SellNeue'
import StoreList from '../view/store/StoreList'
import StoreNeue from '../view/store/StoreNeue'
import StoreNew from '../view/store/StoreNew'
import StripeIntegrationRequired from '../view/StripeIntegrationRequired'
import RoleEdit from '../view/users/RoleEdit'
import Roles from '../view/users/Roles'

const customerProfileChildren = () => [
  ...customerTabRoutes({
    profile: ProfileTab,
    orders: CustomerOrdersTab,
    membership: CustomerMembershipTab,
    settings: CustomerSettingsTab
  }),
  customerRevenueRoute(CustomerRevenueReport)
]
// beforeEach has loaded the user by the time a beforeEnter guard runs.
const parsleyGuard = buildParsleyGuard(() => store.state.loggedInUser)
const searchAndAiGuard = buildSearchAndAiGuard(() => store.state.loggedInUser)
const tierWizardGuards = buildTierWizardGuards(() => store.state.loggedInUser)
const tierListGuard = buildTierListGuard(() => store.state.loggedInUser)
// BOT-1697: plan features hide or lock their routes.
const featureGuard = (key, fallback) => buildFeatureGuard(key, () => store.state.loggedInUser, fallback)
const membershipGuard = featureGuard(MEMBERSHIP_FEATURE, '/sell')
const loyaltyGuard = featureGuard('loyalty', '/sell')
const referralsGuard = featureGuard('referrals', '/sell')
const websiteBuilderGuard = featureGuard('website_builder', '/sell')
const resellersGuard = featureGuard('resellers', '/')
const websiteLeadCaptureGuard = to => (websiteBuilderGuard() === true ? leadCaptureGuard(to) : websiteBuilderGuard())
// Membership routes are plan-gated first; the wizard guards then pick the screen.
const gatedTierGuard = guard => to => (membershipGuard() === true ? guard(to) : membershipGuard())

const routes = [
  {
    path: '/',
    component: StandardLayout,
    redirect: () => privilegeService.getDefaultRoute(store.getters.activePlatform),
    children: [
      {
        path: '/ai/agents',
        meta: { title: 'AI Agents' },
        component: AgentList
      },
      {
        path: '/ai/agents/new/:type?',
        meta: { title: 'New AI Agent' },
        component: AgentForm,
        beforeEnter: to =>
          to.query.type ? { path: newAgentPath(slugForAgentType(to.query.type) ?? ''), replace: true } : true
      },
      {
        path: '/ai/agents/manage',
        redirect: '/ai/agents'
      },
      {
        path: '/ai/agents/settings',
        meta: { title: 'AI agent general settings' },
        component: AgentGeneralSettings
      },
      {
        path: `${AGENT_WIZARD_PATH}/:step?/:store?`,
        meta: { title: 'AI agent setup' },
        component: AgentWizard
      },
      {
        path: '/ai/agents/:id',
        meta: { title: 'AI Agent' },
        component: AgentConversations
      },
      // Anyone who bookmarked the tab under its old name.
      {
        path: '/ai/agents/:id/embed',
        redirect: to => websiteLeadsPath(to.params.id)
      },
      {
        path: '/ai/agents/:id/embed/:channel/report',
        redirect: to => websiteReportPath(to.params.id, to.params.channel)
      },
      {
        path: '/ai/agents/:id/website',
        redirect: to => websiteLeadsPath(to.params.id)
      },
      {
        path: '/ai/agents/:id/lead-form',
        redirect: to => websiteLeadsPath(to.params.id)
      },
      {
        path: '/ai/agents/:id/settings',
        meta: { title: 'AI agent settings' },
        component: AgentForm
      },
      {
        path: '/marketing',
        component: MarketingShell,
        children: [
          { path: '', meta: { title: 'Marketing' }, component: MarketingHome },
          { path: 'history', meta: { title: 'Marketing history' }, component: MarketingHistory },
          { path: 'performance', meta: { title: 'Marketing performance' }, component: MarketingPerformance }
        ]
      },
      {
        path: '/ai/conversations',
        redirect: '/ai/agents'
      },
      {
        path: '/leads',
        meta: { title: 'Leads' },
        component: CateringLeads
      },
      {
        path: '/agent-settings',
        meta: { title: 'Agent settings' },
        component: AgentSettingsIndex
      },
      {
        path: '/agent-settings/new',
        meta: { title: 'Agent settings' },
        component: AgentForm
      },
      {
        path: '/agent-settings/:id',
        meta: { title: 'Agent settings' },
        component: AgentForm
      },
      {
        path: '/agent-settings/:id/website',
        meta: { title: 'Website leads' },
        component: AgentWebsite
      },
      {
        path: '/agent-settings/:id/website/:channel/report',
        meta: { title: 'Website leads report' },
        component: WebsiteChannelReport,
        beforeEnter: to => (['form', 'chat'].includes(to.params.channel) ? true : websiteLeadsPath(to.params.id))
      },
      {
        path: '/dashboard',
        component: DashboardHome,
        meta: { title: 'Dashboard' }
      },
      ...franchiseDayRoutes,
      {
        path: '/franchise/account',
        component: FranchiseAccount,
        meta: { title: 'Account' },
        beforeEnter: franchiseOnlyGuard
      },
      {
        path: '/availability',
        component: FranchiseAvailability,
        meta: { title: 'Product availability' },
        beforeEnter: franchiseOnlyGuard
      },
      {
        path: '/guides',
        meta: {
          title: 'Guides'
        },
        component: GuidesTab
      },
      {
        path: '/settings',
        meta: {
          title: 'Settings'
        },
        component: SettingsIndex
      },
      {
        path: '/settings/account',
        meta: {
          title: 'Account'
        },
        component: SettingsAccount
      },
      {
        path: '/settings/notifications',
        meta: {
          title: 'Notifications'
        },
        component: SettingsNotifications
      },
      {
        path: '/settings/company',
        meta: {
          title: 'Company settings'
        },
        component: SettingsCompany
      },
      {
        path: ORDERING_AND_DELIVERY_PATH,
        meta: {
          title: 'Ordering & delivery'
        },
        component: SettingsOrderingDelivery
      },
      {
        path: '/settings/office-hours',
        meta: {
          title: 'Office hours'
        },
        component: SettingsOfficeHours
      },
      {
        path: '/settings/search-and-ai',
        meta: {
          title: 'Search & AI'
        },
        component: SettingsSearchAndAi,
        beforeEnter: searchAndAiGuard
      },
      {
        path: '/settings/billing',
        meta: {
          title: 'Billing & payouts'
        },
        component: SettingsBilling
      },
      {
        path: '/settings/integrations',
        meta: {
          title: 'Integrations'
        },
        component: SettingsIntegrations
      },
      {
        path: '/messages/:id',
        component: CustomerRecordLayout,
        children: [{ path: '', meta: { title: 'Messages', customerTab: 'messages' }, component: MessagesTab }]
      },
      {
        path: '/profile/:id',
        component: CustomerRecordLayout,
        children: customerProfileChildren()
      },
      {
        path: '/lists/:listid/profile/:id',
        component: CustomerRecordLayout,
        children: customerProfileChildren()
      },
      {
        path: '/messages/starred',
        redirect: '/messages'
      },
      {
        path: '/messages/:tab(unread|sent|upcoming)?',
        meta: {
          title: 'Messages'
        },
        component: MessagesNeue
      },
      {
        path: '/lists/:tab?',
        meta: {
          title: 'Lists'
        },
        component: ListsNeue
      },
      {
        path: '/lists/new',
        meta: {
          title: 'New list'
        },
        component: NewListNeue
      },
      {
        path: '/lists/:id(\\d+)/:tab?',
        meta: {
          title: 'List'
        },
        beforeEnter: tierListGuard,
        component: AudienceNeue
      },
      {
        path: '/resellers',
        meta: { title: 'Resellers' },
        component: ResellerList,
        beforeEnter: resellersGuard
      },
      // BOT-1610: the overview and customers tabs became one page.
      {
        path: '/resellers/:tab(overview|customers)',
        redirect: '/resellers'
      },
      {
        path: '/resellers/:id(\\d+)/:tab?',
        meta: { title: 'Reseller' },
        component: ResellerDetail,
        beforeEnter: resellersGuard
      },
      {
        path: '/orders',
        meta: {
          title: 'Orders'
        },
        component: OrdersNeue
      },
      {
        path: '/order/:id/:tab?',
        meta: {
          title: 'Order'
        },
        component: OrderDetailNeue
      },
      {
        path: '/reports',
        meta: { title: 'Reports' },
        component: ReportsNeue
      },
      ...Object.entries(reportDetailByPath).map(([path, report]) => ({
        path,
        meta: { title: report.title },
        component: ReportDetailPage
      })),
      {
        path: '/store/packing-slips',
        component: PackingSlipSettings
      },
      {
        path: '/sell',
        meta: {
          title: 'Sell'
        },
        component: SellNeue
      },
      {
        path: '/roles',
        redirect: '/users/roles'
      },
      {
        path: '/roles/:id',
        redirect: to => `/users/roles/${to.params.id}`
      },
      {
        path: '/users/roles',
        meta: {
          title: 'Roles'
        },
        component: Roles
      },
      {
        path: '/users/roles/:id',
        meta: {
          title: 'Role'
        },
        component: RoleEdit
      },
      {
        path: '/store',
        redirect: '/store/stores'
      },
      {
        path: '/store/products',
        meta: {
          title: 'Product library'
        },
        component: ProductLibraryRoute
      },
      {
        path: '/store/folders',
        meta: {
          title: 'Folders'
        },
        component: ProductFolders
      },
      {
        path: PARSLEY_PATH,
        meta: {
          title: 'Parsley'
        },
        component: ParsleyMapping,
        beforeEnter: parsleyGuard
      },
      {
        // The same view, so switching tabs neither remounts the page nor
        // checks the connection again.
        path: PARSLEY_NEW_ITEMS_PATH,
        meta: {
          title: 'New from Parsley'
        },
        component: ParsleyMapping,
        beforeEnter: parsleyGuard
      },
      {
        path: '/store/products/:id/:tab?',
        meta: {
          title: 'Product'
        },
        component: ProductNeue
      },
      {
        path: '/store/products/:id/old',
        meta: {
          title: 'Product'
        },
        component: ProductDetail
      },
      {
        path: '/store/products/new',
        meta: {
          title: 'New product'
        },
        component: ProductNew
      },
      {
        path: '/store/stores',
        meta: {
          title: 'Stores'
        },
        component: StoreList
      },
      {
        path: '/store/stores/:id',
        redirect: to => ({ path: `/store/stores/${to.params.id}/main`, hash: to.hash })
      },
      {
        path: '/store/stores/:id/:tab',
        meta: {
          title: 'Store'
        },
        component: StoreNeue
      },
      {
        path: '/store/stores/:id/products/:productId/:tab?',
        meta: {
          title: 'Product'
        },
        component: ProductNeue
      },
      {
        path: '/store/stores/new',
        meta: {
          title: 'New store'
        },
        component: StoreNew
      },
      {
        path: '/store/labels',
        meta: {
          title: 'Labels'
        },
        component: LabelList
      },
      {
        path: '/store/labels/:id',
        meta: {
          title: 'Label'
        },
        component: LabelDetail
      },
      {
        path: '/store/labels/new',
        meta: {
          title: 'New label'
        },
        component: LabelNew
      },
      {
        path: '/store/discount-codes',
        meta: {
          title: 'Discount codes'
        },
        component: DiscountCodeList
      },
      {
        path: '/store/discount-codes/manage',
        meta: {
          title: 'Discount codes'
        },
        component: DiscountCodeList
      },
      {
        path: '/store/discount-codes/:id/:tab?',
        meta: {
          title: 'Discount code'
        },
        component: DiscountCodeDetail
      },
      {
        path: '/store/discount-codes/new',
        meta: {
          title: 'New discount code'
        },
        component: DiscountCodeNew
      },
      {
        path: '/store/gift-cards',
        meta: {
          title: 'Gift cards'
        },
        component: GiftCardList
      },
      {
        path: '/store/gift-cards/:id',
        meta: {
          title: 'Gift card'
        },
        component: GiftCardDetail
      },
      {
        path: '/store/gift-cards/new',
        meta: {
          title: 'New gift gard'
        },
        component: GiftCardNew
      },
      {
        path: '/store/account-credits',
        meta: {
          title: 'Account credits'
        },
        component: AccountCreditList
      },
      {
        path: '/store/fulfillment-methods',
        meta: {
          title: 'Fulfillment methods'
        },
        component: FulfillmentMethodList
      },
      {
        path: '/store/fulfillment-methods/:id/:tab?',
        meta: {
          title: 'Fulfillment method'
        },
        component: FulfillmentMethodNeue
      },
      {
        path: `${FULFILLMENT_WIZARD_PATH}/:step?`,
        meta: {
          title: 'New fulfillment method'
        },
        beforeEnter: () => hasFulfillmentWizard(store.state.loggedInUser) || '/store/fulfillment-methods',
        component: FulfillmentMethodWizard
      },
      {
        path: '/store/fulfillment-methods/new/:step?',
        redirect: to => ({ path: [FULFILLMENT_WIZARD_PATH, to.params.step].filter(Boolean).join('/'), query: to.query })
      },
      {
        path: '/store/membership-tiers',
        meta: {
          title: 'Membership tiers'
        },
        component: MembershipTierList,
        beforeEnter: membershipGuard
      },
      {
        path: '/store/membership-tiers/:id/old',
        meta: {
          title: 'Membership tier'
        },
        component: MembershipTierDetail,
        beforeEnter: membershipGuard
      },
      {
        path: '/store/membership-tiers/:id(\\d+)/:tab(overview|filters|drops|orders|uber-deliveries|carts|products|customers|automations|reports)?',
        meta: {
          title: 'Membership tier'
        },
        component: MembershipTierOverview,
        beforeEnter: membershipGuard
      },
      {
        path: '/store/membership-tiers/:id(\\d+)/edit/:tab?',
        meta: {
          title: 'Membership tier'
        },
        component: MembershipTierRoute,
        beforeEnter: membershipGuard
      },
      {
        path: '/store/membership-tiers/:id(\\d+)/:tab',
        redirect: to => `/store/membership-tiers/${to.params.id}/edit/${to.params.tab}`
      },
      {
        path: '/store/membership-tiers/new',
        meta: {
          title: 'New membership tier'
        },
        component: MembershipTierNew,
        beforeEnter: gatedTierGuard(tierWizardGuards.newTier)
      },
      {
        path: '/store/membership-tiers/setup/:step?',
        meta: {
          title: 'New membership tier'
        },
        component: MembershipTierWizard,
        beforeEnter: gatedTierGuard(tierWizardGuards.wizard)
      },
      {
        path: '/store/drops',
        meta: {
          title: 'Drops'
        },
        component: DropList
      },
      {
        path: '/store/drops/:id/:tab?',
        meta: {
          title: 'Drop'
        },
        component: DropDetail
      },
      {
        path: '/store/drops/new',
        meta: {
          title: 'New drop'
        },
        component: DropNew
      },
      {
        path: `${DROP_WIZARD_PATH}/:step?`,
        meta: {
          title: 'New drop'
        },
        component: DropWizard
      },
      {
        path: '/store/loyalty-program',
        meta: {
          title: 'Loyalty program'
        },
        component: LoyaltyProgramOverview,
        beforeEnter: loyaltyGuard
      },
      {
        path: '/store/loyalty-programs',
        redirect: '/store/loyalty-program'
      },
      {
        path: '/store/loyalty-program/settings',
        meta: {
          title: 'Loyalty program settings'
        },
        component: LoyaltyProgramSettings,
        beforeEnter: loyaltyGuard
      },
      {
        path: '/store/loyalty-programs/:id',
        redirect: '/store/loyalty-program/settings'
      },
      {
        path: '/store/referral-program',
        meta: {
          title: 'Referral program'
        },
        component: ReferralOverview,
        beforeEnter: referralsGuard
      },
      {
        path: '/store/referral-program/codes',
        meta: {
          title: 'Referral codes'
        },
        component: ReferralOverview,
        beforeEnter: referralsGuard
      },
      {
        path: '/store/referral-program/codes/:id',
        meta: {
          title: 'Referral code'
        },
        component: ReferralCodeDetail,
        beforeEnter: referralsGuard
      },
      {
        path: '/store/referral-program/settings',
        meta: {
          title: 'Referral program settings'
        },
        component: ReferralNeue,
        beforeEnter: referralsGuard
      },
      {
        path: '/locked/:key',
        component: FeatureLocked,
        meta: { title: 'Not on your plan' }
      },
      {
        path: '/storefront',
        meta: {
          title: 'Storefront'
        },
        component: StorePrefs,
        beforeEnter: websiteBuilderGuard
      },
      {
        path: '/storefront/domains',
        redirect: { path: '/storefront', hash: '#domains' }
      },
      {
        path: '/storefront/modules',
        meta: {
          title: 'Content blocks'
        },
        component: ModulesNeue,
        beforeEnter: websiteBuilderGuard
      },
      {
        path: '/storefront/links',
        name: 'Links',
        component: LinksNeue,
        meta: { requiresAuth: true },
        beforeEnter: websiteBuilderGuard
      },
      {
        path: '/storefront/lead-capture',
        meta: {
          title: 'Lead capture'
        },
        component: LeadCaptureSettings,
        beforeEnter: websiteLeadCaptureGuard
      },
      {
        // The same view, so switching tabs neither remounts it nor refetches the
        // page. It reads which tab it is on from the route.
        path: '/storefront/lead-capture/embed',
        meta: {
          title: 'Embedded signup form'
        },
        component: LeadCaptureSettings,
        beforeEnter: websiteLeadCaptureGuard
      },
      {
        path: '/storefront/lead-capture/report',
        meta: {
          title: 'Lead capture report'
        },
        component: LeadCaptureReport,
        beforeEnter: websiteLeadCaptureGuard
      },

      {
        path: '/automation',
        redirect: '/automation/drops'
      },
      {
        path: '/automation/drops',
        meta: {
          title: 'Drops'
        },
        component: AutomationDropList
      },
      {
        path: '/automation/workflows',
        meta: {
          title: 'Workflows'
        },
        component: WorkflowList
      },
      {
        path: '/automation/scheduled-messages',
        meta: {
          title: 'Scheduled messages'
        },
        component: ScheduledMessageList
      },
      {
        path: '/automation/sent-messages',
        meta: {
          title: 'Sent messages'
        },
        component: SentMessageList
      },
      {
        path: '/automation/sent-messages/:id',
        meta: {
          title: 'Sent message'
        },
        component: SentMessageDetail
      },
      {
        path: '/automation/message/:id',
        meta: {
          title: 'Automation',
          automation: 'message'
        },
        component: AutomationNeue
      },
      {
        path: '/automation/drop/:id',
        meta: {
          title: 'Automation',
          automation: 'drop'
        },
        component: AutomationNeue
      },
      {
        path: '/automation/workflow/:id',
        meta: {
          title: 'Automation',
          automation: 'workflow'
        },
        component: AutomationNeue
      }
    ]
  },
  {
    path: '/sessions/new',
    component: Auth,
    meta: { noAuth: true }
  },
  {
    path: '/login',
    component: Login,
    meta: {
      title: 'Login',
      noAuth: true
    }
  },
  {
    path: '/legacy-login',
    component: Login,
    props: { legacy: true },
    meta: {
      title: 'Login',
      noAuth: true
    }
  },
  {
    path: '/forgot-password',
    component: ForgotPassword,
    meta: {
      title: 'Forgot password',
      noAuth: true
    }
  },
  {
    path: '/forgot-password-email',
    component: ForgotPasswordEmail,
    meta: {
      title: 'Forgot password',
      noAuth: true
    }
  },
  {
    path: '/system_user/:id/reset_password',
    component: SetPassword,
    meta: {
      title: 'Set password',
      noAuth: true
    }
  },
  {
    path: '/sign-up',
    component: SignupEmail,
    meta: {
      title: 'Sign up',
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/password',
    component: SignupPassword,
    meta: {
      title: 'Password',
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/business-name',
    component: SignupBusinessName,
    meta: {
      title: 'BusinessName',
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/business-type',
    component: SignupBusinessType,
    meta: {
      // title: 'BusinessType',
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/area-code',
    component: SignupAreaCode,
    meta: {
      // title: 'AreaCode',
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/referral',
    component: SignupReferral,
    meta: {
      noAuth: true,
      noindex: true
    }
  },
  {
    path: '/stripe-integration-required',
    component: StripeIntegrationRequired,
    meta: {
      noAuth: true
    }
  },
  {
    path: '/restart-stripe-integration',
    component: RestartStripeIntegration,
    meta: {
      noAuth: true
    }
  },
  {
    path: '/pay-past-due-stripe-balance',
    component: PayPastDueStripeBalance,
    meta: {
      noAuth: true
    }
  },
  {
    path: '/confirm-email-address',
    component: ConfirmEmailAddress,
    meta: {
      noAuth: true,
      title: 'Confirm email'
    }
  },
  {
    path: '/catering/get-started',
    name: 'CateringGetStarted',
    component: () => import('@/view/catering/getstarted/CateringGetStarted.vue'),
    meta: { noAuth: true, noindex: true }
  },
  {
    path: '/e-commerce/get-started',
    name: 'EcommerceGetStarted',
    component: () => import('@/view/ecommerce/getstarted/EcommerceGetStarted.vue'),
    meta: { noAuth: true, noindex: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

const appName = 'Bottle Dashboard'

function updateMetaTags(meta) {
  const existingRobotsMeta = document.querySelector('meta[name="robots"]')
  if (existingRobotsMeta) {
    existingRobotsMeta.remove()
  }

  if (meta.noindex) {
    const robotsMeta = document.createElement('meta')
    robotsMeta.name = 'robots'
    robotsMeta.content = 'noindex, nofollow'
    document.head.appendChild(robotsMeta)
  }
}

router.beforeEach(async (to, from, next) => {
  // Signup attribution: remember the utm params a visitor landed with so the
  // get-started journeys can credit the campaign when the account is minted.
  captureSignupAttribution(to.query)
  captureReferralCode(to.query)
  // Meta
  document.title = to.meta.title ? `${to.meta.title} | ${appName}` : appName
  updateMetaTags(to.meta)
  if (to.path === '/confirm-email-address' && Cookies.get('auth_token')) {
    try {
      if (!store.state.loggedInUser?.attributes) {
        await store.dispatch('getLoggedInUser')
      }
      if (store.state.loggedInUser?.attributes?.converted_to_email_signin) {
        next({ path: privilegeService.getDefaultRoute(store.getters.activePlatform), replace: true })
        return
      }
    } catch (_) {}
  }
  // Auth Requirements
  if (!(to.matched.some(record => record.meta.noAuth) || to.path === '/') && !Cookies.get('auth_token')) {
    next({
      path: '/login',
      params: { redirect: to.fullPath }
    })
  } else {
    if (Cookies.get('auth_token') && !to.matched.some(record => record.meta.noAuth)) {
      if (!privilegeService.canAccessRoute(to.path)) {
        next({
          path: '/',
          replace: true
        })
        return
      }
      if (!store.state.loggedInUser?.attributes && !store.state.franchise) {
        try {
          await store.dispatch('getLoggedInUser')
        } catch (_) {} // eslint-disable-line no-empty
      }
      if (!canAccessRouteForPlatform(to.path, store.getters.activePlatform)) {
        next({ path: privilegeService.getDefaultRoute(store.getters.activePlatform), replace: true })
        return
      }
    }
    next()
  }
})
router.afterEach((to, from) => {
  fbq('track', 'PageView')
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    url: to.fullPath,
    path: to.path,
    route_name: to.name
  })
})

export default router
